/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2024-2026. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: PageUtil.ts
 * Created by: chan.ying
 * Create Date: 2026-03-18 17:00
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import ProfileData from "@/utils/cookie/ProfileData";

export default class PageUtil {

    // ---------------------------------------------------------------------------------------

    public static encodeCursor(item: any): string {
        if (!item) return "";
        const payload = {
            createdAt: item.createdAt,
            id: item.id
        };
        return btoa(JSON.stringify(payload))
            .replace(/\+/g, '-')
            .replace(/\//g, '_');
    }
}

export class PaginationResponse<T> {
    public data: T[] = []
    public pagination: PageUtil = new PageUtil()
}

export class Pagination {
    public pageSize: number = 0
    public count: number = 0
    public pageTotal: number = 0
    public hasNext: boolean = false
    public hasPrevious: boolean = false
    public firstCreatedAt: string = ""
    public firstId: string = ""
    public lastCreatedAt: string = ""
    public lastId: string = ""
}

export interface PageParamsOptions {
    /** search keyword */
    search?: string;

    /** Cursor-based pagination - backward */
    after?: string | number;

    /** Cursor-based pagination - forward */
    before?: string | number;

    /** Number per page, default 10 */
    limit?: number;

    /** Other arbitrary additional parameters (will be automatically combined) */
    [key: string]:
        | string
        | number
        | boolean
        | string[]
        | number[]
        | boolean[]
        | undefined;
}

/**
 * Construct pagination query parameters
 */
export function buildPageParams(
    options: PageParamsOptions = {}
): URLSearchParams {
    const {
        search,
        after,
        before,
        limit = ProfileData.getPageSizeKey(),
        ...rest
    } = options;

    const params = new URLSearchParams();

    // underlying parameter
    params.set('limit', String(limit));

    if (search) params.set('q', search);
    if (after != null) params.set('after', String(after));
    if (before != null) params.set('before', String(before));

    // Handle the remaining general parameters (supporting arrays)
    Object.entries(rest).forEach(([key, value]) => {
        if (value == null || value === '') return;

        if (Array.isArray(value)) {
            // Array type → Use append to support multiple values
            value.forEach(v => {
                if (v != null && v !== '') {
                    params.append(key, String(v));
                }
            });
        } else {
            // Ordinary type → Use set
            params.set(key, String(value));
        }
    });

    return params;
}